import * as React from "react";
import { ChevronDownIcon, XIcon } from "lucide-react";

import { cn } from "@/src/js/lib/utils";
import { Badge } from "../../ui/badge";
import { Checkbox } from "../../ui/checkbox";
import {
  DROPDOWN_CONTENT_CLASS,
  DROPDOWN_ITEM_CLASS,
  DROPDOWN_TRIGGER_CLASS,
} from "../../ui/dropdown-select";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "../../ui/popover";

export interface FilterMultiSelectOption {
  name: string;
  value: string;
  // How many records the option has given the other filters, shown after the name
  count?: number;
}

interface FilterMultiSelectProps {
  label: string;
  // The checked options. Every option starts checked, like the Data tab's filters
  value: string[];
  options: FilterMultiSelectOption[];
  onChange: (value: string[]) => void;
  // Shown while every option is checked, so it should read like "All …"
  placeholder?: string;
}

// The Data tab's multiple select shows a search box once a list is longer than this
const MAX_OPTIONS_WITHOUT_SEARCH = 9;

// Preflight is off, so a plain button keeps the browser's own border, padding and shadow. These are
// inline so no stylesheet rule for buttons can bring them back
const BARE_BUTTON_STYLE: React.CSSProperties = {
  appearance: "none",
  border: 0,
  padding: 0,
  backgroundColor: "transparent",
  boxShadow: "none",
};

// The app's dark link blue ($link-dark-blue), which the Data tab uses for "Select all" and "Only"
const LINK_CLASS =
  "tw:cursor-pointer tw:text-[#17436d] tw:underline tw:disabled:cursor-default tw:disabled:no-underline tw:disabled:opacity-50";

// Works like the Data tab's multiple select (pill chips, a list of checkboxes, Select all and Only) so
// both screens feel the same to clients, but is styled like the dashboard's other dropdowns. Like its smart filters: options with a
// count of 0 are hidden from the list, and every checked option shows as a removable chip, even when
// it's hidden. Checking everything is the same as no filter.
export function FilterMultiSelect({
  label,
  value,
  options,
  onChange,
  placeholder = "All",
}: FilterMultiSelectProps) {
  const toggleId = React.useId();
  const optionsLegendId = React.useId();
  const triggerBoxRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchText, setSearchText] = React.useState("");

  const checkedOptions = options.filter((option) => value.includes(option.value));
  const allChecked = checkedOptions.length === options.length;
  const noneChecked = checkedOptions.length === 0;
  const isSearchable = options.length > MAX_OPTIONS_WITHOUT_SEARCH;
  const selectionSummary = allChecked
    ? placeholder
    : noneChecked
      ? "None selected"
      : checkedOptions.map((option) => option.name).join(", ");
  const shownOptions = options
    .filter((option) => option.count !== 0)
    .filter((option) => option.name.toLowerCase().includes(searchText.trim().toLowerCase()));

  const toggle = (optionValue: string, checked: boolean) => {
    onChange(
      checked ? [...value, optionValue] : value.filter((selected) => selected !== optionValue),
    );
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setSearchText("");
  };

  return (
    <div className="tw:grid tw:gap-2">
      {/* Clicking a label clicks its control, but the other filters' titles do nothing when clicked */}
      <label
        htmlFor={toggleId}
        className="tw:text-sm tw:font-medium tw:leading-none"
        onClick={(event) => event.preventDefault()}
      >
        {label}
      </label>
      <div className="tw:w-[260px] tw:max-w-full tw:text-sm tw:text-neutral-950">
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
          <PopoverAnchor asChild>
            <div
              ref={triggerBoxRef}
              className={cn(
                DROPDOWN_TRIGGER_CLASS,
                "tw:h-auto tw:min-h-9 tw:w-full tw:cursor-pointer tw:py-1 tw:pr-0 tw:has-[button:focus-visible]:border-neutral-950 tw:has-[button:focus-visible]:ring-[3px] tw:has-[button:focus-visible]:ring-neutral-950/50",
              )}
              onClick={(event) => {
                // The chips' remove buttons and the toggle button handle their own clicks
                if (!(event.target as HTMLElement).closest("button")) setIsOpen(true);
              }}
            >
              <div className="tw:flex tw:max-h-[100px] tw:min-w-0 tw:flex-1 tw:flex-wrap tw:items-center tw:overflow-y-auto tw:py-0.5">
                {allChecked || noneChecked ? (
                  <div className="tw:truncate tw:text-neutral-500">{selectionSummary}</div>
                ) : (
                  <ul className="tw:m-0 tw:flex tw:w-full tw:min-w-0 tw:list-none tw:flex-wrap tw:items-center tw:p-0">
                    {checkedOptions.map((option) => (
                      <SelectedChip
                        key={option.value}
                        name={option.name}
                        onRemove={() => toggle(option.value, false)}
                      />
                    ))}
                  </ul>
                )}
              </div>
              <PopoverTrigger asChild>
                <button
                  id={toggleId}
                  type="button"
                  aria-label={`${label}: ${selectionSummary}`}
                  style={BARE_BUTTON_STYLE}
                  className="tw:flex tw:shrink-0 tw:cursor-pointer tw:items-center tw:self-stretch tw:pr-3 tw:text-neutral-500 tw:outline-none"
                >
                  <ChevronDownIcon className="tw:size-4 tw:opacity-50" />
                </button>
              </PopoverTrigger>
            </div>
          </PopoverAnchor>

          <PopoverContent
            align="start"
            aria-labelledby={optionsLegendId}
            className={cn(
              DROPDOWN_CONTENT_CLASS,
              "tw:w-(--radix-popover-trigger-width) tw:text-sm",
            )}
            // A press inside the trigger box (chips, remove buttons) isn't an outside press
            onInteractOutside={(event) => {
              if (triggerBoxRef.current?.contains(event.target as Node)) event.preventDefault();
            }}
          >
            <fieldset className="tw:m-0 tw:min-w-0 tw:border-0 tw:p-0">
              <legend id={optionsLegendId} className="tw:sr-only">{`${label} options`}</legend>
              {isSearchable && (
                <div className="tw:mb-1 tw:border-b tw:border-neutral-200 tw:px-2 tw:pb-2">
                  <input
                    type="search"
                    aria-label={`Search ${label}`}
                    placeholder="Search..."
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    className="tw:w-full tw:border-0 tw:text-sm tw:outline-none"
                  />
                </div>
              )}
              <div className="tw:flex tw:gap-4 tw:px-2 tw:py-1.5">
                <button
                  type="button"
                  style={BARE_BUTTON_STYLE}
                  className={LINK_CLASS}
                  disabled={allChecked}
                  onClick={() => onChange(options.map((option) => option.value))}
                >
                  Select all
                </button>
                <button
                  type="button"
                  style={BARE_BUTTON_STYLE}
                  className={LINK_CLASS}
                  disabled={noneChecked}
                  onClick={() => onChange([])}
                >
                  Deselect all
                </button>
              </div>
              <ul className="tw:m-0 tw:list-none tw:p-0">
                {shownOptions.map((option) => (
                  <li
                    key={option.value}
                    className={cn(
                      DROPDOWN_ITEM_CLASS,
                      "tw:group tw:hover:bg-neutral-100 tw:focus-within:bg-neutral-100 tw:cursor-pointer tw:pr-12",
                    )}
                  >
                    <label className="tw:mb-0 tw:flex tw:flex-1 tw:cursor-pointer tw:items-start tw:font-normal">
                      <Checkbox
                        className="tw:mt-[3px]"
                        checked={value.includes(option.value)}
                        onCheckedChange={(checked) => toggle(option.value, checked === true)}
                      />
                      <span className="tw:ml-2">{getOptionText(option)}</span>
                    </label>
                    <button
                      type="button"
                      aria-label={`Only ${option.name}`}
                      style={BARE_BUTTON_STYLE}
                      className={`${LINK_CLASS} tw:absolute tw:top-1/2 tw:right-2 tw:invisible tw:-translate-y-1/2 tw:bg-neutral-100 tw:group-hover:visible tw:group-focus-within:visible`}
                      onClick={() => onChange([option.value])}
                    >
                      Only
                    </button>
                  </li>
                ))}
              </ul>
            </fieldset>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

function getOptionText(option: FilterMultiSelectOption): string {
  return option.count === undefined ? option.name : `${option.name} (${option.count})`;
}

function SelectedChip({ name, onRemove }: { name: string; onRemove: () => void }) {
  return (
    <li className="tw:my-0.5 tw:mr-1 tw:max-w-[calc(100%-14px)]">
      <Badge
        variant="outline"
        className="tw:max-w-full tw:py-0 tw:pr-1.5 tw:pl-3 tw:text-sm tw:font-normal"
      >
        <span className="tw:truncate">{name}</span>
        <button
          type="button"
          aria-label={`Remove ${name}`}
          style={BARE_BUTTON_STYLE}
          className="tw:flex tw:size-5 tw:shrink-0 tw:cursor-pointer tw:items-center tw:justify-center tw:text-[#5C5F62]"
          onClick={onRemove}
        >
          <XIcon className="tw:size-3" aria-hidden="true" />
        </button>
      </Badge>
    </li>
  );
}
